import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import PagesLayout from 'layout/Pages';
import SimpleLayout from 'layout/Simple';

import { SimpleLayoutType } from 'config';
import ProtectedRoute from 'components/ProtectedRoute';
import DefaultDashboardRedirect from 'components/DefaultDashboardRedirect';
import { Outlet } from 'react-router';
import AppErrorElement from './AppErrorElement';
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon')));

const AppContactUS = Loadable(lazy(() => import('pages/contact-us')));

const OverviewPage = Loadable(lazy(() => import('pages/administration/overview/DashboardOverview')));
const ModuleAssetInventoryPage = Loadable(lazy(() => import('pages/modules/assetInventory/Inventory')));
const WorkOrderCreate = Loadable(lazy(() => import('pages/modules/workOrder/CreateTask')));
const WorkOrderTasks = Loadable(lazy(() => import('pages/modules/workOrder/TaskManagement')));
const WorkOrderMyTasks = Loadable(lazy(() => import('pages/modules/workOrder/MyTasks')));
const WorkOrderTaskTypes = Loadable(lazy(() => import('pages/modules/workOrder/TaskTypeManagement')));
const WorkOrderTaskTypeForm = Loadable(lazy(() => import('pages/modules/workOrder/TaskTypeFormPage')));
const OperationalTreatmentAssignmentsPage = Loadable(lazy(() => import('pages/modules/operationalPlanning/TreatmentAssignments')));
const OperationalCitySummaryPage = Loadable(lazy(() => import('pages/modules/operationalPlanning/TreatmentCitySummary')));
const OperationalPlanningPage = Loadable(lazy(() => import('pages/modules/operationalPlanning/Index')));
const ScenarioSettingPage = Loadable(lazy(() => import('pages/modules/planning/scenarioSetting/ScenarioSetting')));
const ScenarioAnalysisPage = Loadable(lazy(() => import('pages/modules/planning/scenarioAnalysis/ScenarioAnalysis')));
const ScenarioComparisonPage = Loadable(lazy(() => import('pages/modules/planning/scenarioComparison/scenarioComparison')));
const ScenarioPlaygroundPage = Loadable(lazy(() => import('pages/modules/planning/ScenarioPlayground/ScenarioPlayground')));
const ScenarioConfigPage = Loadable(lazy(() => import('pages/modules/planning/scenarioConfig/ScenarioConfig')));
const StatusPage = Loadable(lazy(() => import('pages/modules/reports/Status/Status')));
const StatusDetailPage = Loadable(lazy(() => import('pages/modules/reports/Status/StatusDetail')));
const WorkOrderEditTask = Loadable(lazy(() => import('pages/modules/workOrder/EditTask')));
const WorkOrderTaskComplete = Loadable(lazy(() => import('pages/modules/workOrder/TaskCompleteForm')));
const WorkOrderTaskView = Loadable(lazy(() => import('pages/modules/workOrder/TaskDetails')));
const GapAnalysisPage = Loadable(lazy(() => import('pages/modules/reports/gapAnalysis/GapAnalysis')));
const UploadCenterPage = Loadable(lazy(() => import('pages/administration/uploadCenter/componentsUploadCenter/Index')));
const FolderPage = Loadable(lazy(() => import('pages/administration/uploadCenter/componentsUploadCenter/FolderAssetName')));
const FilePage = Loadable(lazy(() => import('pages/administration/uploadCenter/componentsUploadCenter/AssetDataUpload')));
const AccountProfile = Loadable(lazy(() => import('pages/administration/clientProfile/account')));
const AccountTabProfile = Loadable(lazy(() => import('sections/profiles/account/TabProfile')));
const AccountTabPersonal = Loadable(lazy(() => import('sections/profiles/account/TabPersonal')));
const AccountTabAccount = Loadable(lazy(() => import('sections/profiles/account/TabAccount')));
const AccountTabPassword = Loadable(lazy(() => import('sections/profiles/account/TabPassword')));
const AccountTabRole = Loadable(lazy(() => import('sections/profiles/account/TabRole')));
const AccountTabSettings = Loadable(lazy(() => import('sections/profiles/account/TabSettings')));
const MultiAssetViewer = Loadable(lazy(() => import('pages/modules/assetViewer2/MultiAssetViewer')));
const FullInfoAsset = Loadable(lazy(() => import('pages/modules/assetViewer2/assetInfo/FullInfo')));
const AssetInventoryPage = Loadable(lazy(() => import('pages/administration/assetInventory/Inventory')));
const AssetCreatePage = Loadable(lazy(() => import('pages/administration/assetInventory/CreateAsset')));
const IntegrationCenterPage = Loadable(lazy(() => import('pages/administration/integrationCenter/Integration')));
const AssetDataManagementPage = Loadable(lazy(() => import('pages/administration/assetInventory/DataManagement')));
const NonAssetDataPage = Loadable(lazy(() => import('pages/administration/nonAssetData/NonAssetData')));
const SpatialIntelligencePage = Loadable(lazy(() => import('pages/administration/spatialIntelligence/SpatialIntelligence')));
const PreferencesPage = Loadable(lazy(() => import('pages/administration/preferences/assetHierarchy/index')));
const PreferencePage = Loadable(lazy(() => import('pages/administration/preferences/standardFields/Index')));
const BackupSettingsPage = Loadable(lazy(() => import('pages/administration/preferences/backupSettings/index')));
const AccessControl = Loadable(lazy(() => import('pages/administration/preferences/accessControlPage/accessControl')));
const TreatmentMenuPage = Loadable(lazy(() => import('pages/administration/preferences/standardTreatment/index')));
const FeatureTwoPreferences = Loadable(lazy(() => import('pages/administration/preferences/newPagesPreferences/FeatureTwo')));
const EditTreatmentPage = Loadable(lazy(() => import('pages/administration/preferences/standardTreatment/component/EditTreatmentPage')));
const AddTreatmentPage = Loadable(lazy(() => import('pages/administration/preferences/standardTreatment/component/AddTreatmentPage')));
const FeatureOnePreferences = Loadable(lazy(() => import('pages/administration/preferences/levelOfServiceSetting/Index')));
const GeneralSettingPage = Loadable(lazy(() => import('pages/administration/preferences/generalSetting/index')));
const MenuCustomizationPage = Loadable(lazy(() => import('pages/administration/preferences/menuCustomization/MenuCustomization')));
const RiskSettingsPage = Loadable(lazy(() => import('pages/administration/preferences/riskSetting/Index')));
const RoadAssessmentPage = Loadable(lazy(() => import('pages/modules/reports/RoadAssessment/index')));
const AssetValuationPage = Loadable(
  lazy(() => import('pages/administration/preferences/assetValuationSetting/ExtraFeaturesValuationPage'))
);
const ValuationName = Loadable(lazy(() => import('pages/administration/preferences/assetValuationSetting')));
const DeteriorationModels = Loadable(lazy(() => import('pages/administration/preferences/deteriorationModels/Index')));
const VisualPreferences = Loadable(lazy(() => import('pages/administration/preferences/visualPreferences/Index')));
const SuperAdminOverviewPage = Loadable(lazy(() => import('pages/super-admin/Overview')));
const SuperAdminCitiesPage = Loadable(lazy(() => import('pages/super-admin/Cities')));
const SuperAdminConsultantsPage = Loadable(lazy(() => import('pages/super-admin/Consultants')));
const SuperAdminRegisterPage = Loadable(lazy(() => import('pages/super-admin/RegisterSuperAdmin')));
const ConsultantOverviewPage = Loadable(lazy(() => import('pages/consultant/Overview')));
const ExtraFeaturesThreePage = Loadable(lazy(() => import('pages/administration/extraFeatures/assetDetectionGS/AssetDetection')));
const AssetDetectionPage = Loadable(lazy(() => import('pages/administration/extraFeatures/assetDetection/AssetDetection')));
const PavementInspectionPage = Loadable(lazy(() => import('pages/administration/extraFeatures/pavementInspection/PavementInspection')));
const DamageDetectionPage = Loadable(lazy(() => import('pages/administration/extraFeatures/damageDetection/DamageDetection')));
const InventoryCreatorIndex = Loadable(lazy(() => import('pages/administration/extraFeatures/inventoryCreator/InventoryCreatorIndex')));
const Feature33 = Loadable(lazy(() => import('pages/administration/extraFeatures/deteriorationModelling/index')));
const Deterioration = Loadable(
  lazy(() => import('pages/administration/extraFeatures/deteriorationModelling/EqCreator/DeteriorationModelPage'))
);
const EsriIntegration = Loadable(lazy(() => import('pages/administration/extraFeatures/newPages/EsriIntegration')));
const BuildingModelling = Loadable(lazy(() => import('pages/administration/extraFeatures/newPages/BuildingModelling')));
const BuildingViewer = Loadable(lazy(() => import('pages/administration/extraFeatures/newPages/BuildingViewer')));
const FacilityViewer = Loadable(lazy(() => import('pages/administration/extraFeatures/newPages/FacilityViewer')));
const EditGeometryPage = Loadable(lazy(() => import('pages/modules/assetViewer2/edit/EditGeometry')));

const AnalyzePage = Loadable(lazy(() => import('pages/administration/uploadCenter/analyzeAiPage/Index')));

const MainRoutes = {
  path: '/',
  errorElement: <AppErrorElement />,
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          element: <ProtectedRoute />,
          children: [
            {
              index: true,
              element: <DefaultDashboardRedirect />
            },
            {
              path: 'super-admin',
              element: <Outlet />,
              children: [
                {
                  index: true,
                  element: <SuperAdminOverviewPage />
                },
                {
                  path: 'cities',
                  element: <SuperAdminCitiesPage />
                },
                {
                  path: 'consultants',
                  element: <SuperAdminConsultantsPage />
                },
                {
                  path: 'register',
                  element: <SuperAdminRegisterPage />
                }
              ]
            },

            {
              path: 'consultant',
              element: <ConsultantOverviewPage />
            },
            {
              path: 'asset-detection-gs',
              element: <ExtraFeaturesThreePage />
            },
            {
              path: 'asset-detection',
              element: <AssetDetectionPage />
            },
            {
              path: 'overview',
              element: <OverviewPage />
            },
            {
              path: 'edit-geometry',
              element: <EditGeometryPage />
            },
            {
              path: `analyze`,
              element: <AnalyzePage />
            },
            {
              path: 'pavement-inspection',
              element: <PavementInspectionPage />
            },
            {
              path: 'deterioration-models/*',
              element: <DeteriorationModels />
            },

            {
              path: 'asset-valuation-setting/:name',
              element: <ValuationName />
            },
            {
              path: 'building-modelling',
              element: <BuildingModelling />
            },
            {
              path: 'building-viewer',
              element: <BuildingViewer />
            },
            {
              path: 'facility-viewer',
              element: <FacilityViewer />
            },
            {
              path: 'deterioration-modelling',
              element: <Feature33 />
            },
            {
              path: 'esri-integration',
              element: <EsriIntegration />
            },
            {
              path: 'deterioration-modelling/:name',
              element: <Deterioration />
            },
            {
              path: 'damage-detection',
              element: <DamageDetectionPage />
            },
            {
              path: 'visual-preferences',
              element: <VisualPreferences />
            },
            {
              path: 'asset-valuation-setting',
              element: <AssetValuationPage />
            },
            {
              path: 'menu-customization',
              element: <MenuCustomizationPage />
            },
            {
              path: 'Feature-Two-Preferences',
              element: <FeatureTwoPreferences />
            },
            {
              path: 'level-of-service-setting',
              element: <FeatureOnePreferences />
            },
            {
              path: 'integration-center',
              element: <IntegrationCenterPage />
            },

            {
              path: 'inventory-creator',
              element: <InventoryCreatorIndex />
            },
            {
              path: 'data-management',
              element: <AssetDataManagementPage />
            },
            {
              path: 'non-asset-data',
              element: <NonAssetDataPage />
            },
            {
              path: 'spatial-intelligence',
              element: <SpatialIntelligencePage />
            },
            {
              path: 'general-setting',
              element: <GeneralSettingPage />
            },
            {
              path: 'add-new-asset',
              element: <AssetCreatePage />
            },
            {
              path: 'upload-center',
              element: <UploadCenterPage />
            },
            {
              path: 'road-assessment',
              element: <RoadAssessmentPage />
            },
            {
              path: 'add-treatment',
              element: <AddTreatmentPage />
            },
            {
              path: 'edit-treatment',
              element: <EditTreatmentPage />
            },
            {
              path: `upload-center/:title`,
              element: <FolderPage />
            },

            {
              path: 'asset-viewer',
              element: <MultiAssetViewer />
            },
            {
              path: `upload-center/:title/:filename`,
              element: <FilePage />
            },
            {
              path: 'asset-inventory',
              element: <ModuleAssetInventoryPage />
            },
            {
              path: 'work-order/create',
              element: <WorkOrderCreate />
            },
            {
              path: 'standard-treatment',
              element: <TreatmentMenuPage />
            },
            {
              path: 'asset-inventory-table',
              element: <AssetInventoryPage />
            },
            {
              path: `full-info-asset`,
              element: <FullInfoAsset />
            },
            {
              path: 'work-order/tasks',
              element: <WorkOrderTasks />
            },
            {
              path: 'work-order/tasks/:id/edit',
              element: <WorkOrderEditTask />
            },
            {
              path: 'work-order/tasks/:id/complete',
              element: <WorkOrderTaskComplete />
            },
            {
              path: 'work-order/tasks/:id/view',
              element: <WorkOrderTaskView />
            },
            {
              path: 'work-order/my-tasks',
              element: <WorkOrderMyTasks />
            },
            {
              path: 'work-order/task-types',
              element: <WorkOrderTaskTypes />
            },
            {
              path: 'work-order/task-types/create',
              element: <WorkOrderTaskTypeForm />
            },
            {
              path: 'work-order/task-types/:id/edit',
              element: <WorkOrderTaskTypeForm />
            },
            {
              path: 'operational-planning/assignments',
              element: <OperationalTreatmentAssignmentsPage />
            },
            {
              path: 'operational-planning/city-summary',
              element: <OperationalCitySummaryPage />
            },
            {
              path: 'operational-planning',
              element: <OperationalPlanningPage />
            },
            {
              path: 'scenario-setting',
              element: <ScenarioSettingPage />
            },
            {
              path: 'scenario-analysis',
              element: <ScenarioAnalysisPage />
            },
            {
              path: 'scenario-config',
              element: <ScenarioConfigPage />
            },
            {
              path: 'scenario-comparison',
              element: <ScenarioComparisonPage />
            },
            {
              path: 'scenario-playground',
              element: <ScenarioPlaygroundPage />
            },
            {
              path: 'status',
              element: <StatusPage />
            },
            {
              path: 'status/:asset',
              element: <StatusDetailPage />
            },
            {
              path: 'gap-analysis',
              element: <GapAnalysisPage />
            },
            {
              path: 'asset-hierarchy',
              element: <PreferencesPage />
            },
            {
              path: 'standard-fields',
              element: <PreferencePage />
            },
            {
              path: 'backup-settings',
              element: <BackupSettingsPage />
            },
            {
              path: 'risk-setting',
              element: <RiskSettingsPage />
            },
            {
              path: 'access-control',
              element: <AccessControl />
            }
          ]
        },
        {
          path: 'profiles',
          children: [
            {
              path: 'account',
              element: <AccountProfile />,
              children: [
                {
                  path: 'basic',
                  element: <AccountTabProfile />
                },
                {
                  path: 'personal',
                  element: <AccountTabPersonal />
                },
                {
                  path: 'my-account',
                  element: <AccountTabAccount />
                },
                {
                  path: 'password',
                  element: <AccountTabPassword />
                },
                {
                  path: 'role',
                  element: <AccountTabRole />
                },
                {
                  path: 'settings',
                  element: <AccountTabSettings />
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/maintenance',
      element: <PagesLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        }
      ]
    },
    {
      path: '/',
      element: <SimpleLayout layout={SimpleLayoutType.SIMPLE} />,
      children: [
        {
          path: 'contact-us',
          element: <AppContactUS />
        }
      ]
    }
  ]
};

export default MainRoutes;
